import React from "react";
import { createPortal } from "react-dom";
import {
  X, User, Briefcase, IdCard, Phone, Hash, Building2, UserCog,
  ShieldCheck, BookOpen, CheckCircle2, Clock, XCircle, FileQuestion,
  Check, Minus, StickyNote, Paperclip, Edit3, ArrowRightLeft, Trash2,
} from "lucide-react";
import { formatHijri, statusOf, daysUntilHijri } from "@/lib/hijri";
import { useT } from "@/lib/i18n";

const STATUS_ICON = {
  valid: { cls: "status-valid", Icon: CheckCircle2 },
  warning: { cls: "status-warning", Icon: Clock },
  expired: { cls: "status-expired", Icon: XCircle },
  missing: { cls: "status-missing", Icon: FileQuestion },
};

function StatusBadge({ hijri, t }) {
  const s = statusOf(hijri);
  const cfg = STATUS_ICON[s];
  const d = daysUntilHijri(hijri);
  let suffix = "";
  if (s === "warning") suffix = ` (${d}${t("days_short")})`;
  if (s === "expired") suffix = ` (${Math.abs(d)}${t("days_short")})`;
  return (
    <span className={`pill ${cfg.cls}`}>
      <cfg.Icon size={12} /> {t(`status_${s}`)}{suffix}
    </span>
  );
}

function BoolPill({ value, t }) {
  return value ? (
    <span className="pill status-valid"><Check size={12} /> {t("yes")}</span>
  ) : (
    <span className="pill status-missing"><Minus size={12} /> {t("no")}</span>
  );
}

function Row({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-center gap-2.5 py-2 border-b border-gray-100 last:border-0" data-testid={testid}>
      <div className="bg-teal-50 text-teal-700 rounded-md p-1.5 shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-gray-500 shrink-0">{label}</div>
        <div className="text-sm text-gray-900 font-semibold break-words text-end min-w-0">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 px-3 py-1.5 mb-3">
      <h4 className="text-[11px] font-bold text-teal-800 uppercase tracking-wider px-1 pt-1.5 pb-1 border-b border-gray-100 mb-1">
        {title}
      </h4>
      {children}
    </div>
  );
}

export default function EmployeeDetails({ employee, branch, adminName, onClose, onEdit, onOpenAttachments, onMove, onDelete, hideCerts = false, extraSections = null }) {
  const { t } = useT();
  if (!employee) return null;

  const e = employee;
  const dash = t("details_no_value");
  const onVacation = !!branch?.is_vacation;
  const attachCount = (e.attachments || []).length;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop animate-fade-in"
      onClick={onClose}
      data-testid="employee-details-modal"
    >
      <div
        className="bg-gray-50 w-full sm:max-w-2xl h-[92vh] sm:max-h-[90vh] sm:h-auto rounded-t-2xl sm:rounded-2xl card-shadow overflow-y-auto flex flex-col"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header — sticky, compact on mobile */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 sm:px-6 py-3 flex items-center justify-between gap-2 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-teal-900">
              <User size={16} className="shrink-0" />
              <h3 className="text-sm sm:text-base font-bold truncate" data-testid="emp-details-name">{e.name}</h3>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{t("employee_details")}</p>
          </div>
          <button
            type="button"
            className="bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 rounded-full p-2 shrink-0 transition-colors"
            onClick={onClose}
            data-testid="emp-details-close-button"
            aria-label={t("cancel")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="px-3 sm:px-5 py-3 flex-1">
          <Section title={t("details_basic")}>
            <Row icon={User} label={t("name")} testid="emp-detail-name">{e.name || dash}</Row>
            <Row icon={Hash} label={t("employee_id")} testid="emp-detail-id">{e.employee_id || dash}</Row>
            <Row icon={IdCard} label={t("iqama_number")} testid="emp-detail-iqama">{e.iqama_number || dash}</Row>
            <Row icon={Phone} label={t("employee_phone")} testid="emp-detail-phone">
              {e.phone ? (
                <a
                  href={`tel:${e.phone}`}
                  className="text-teal-700 hover:underline"
                  style={{ direction: "ltr", unicodeBidi: "embed" }}
                  data-testid="emp-detail-phone-link"
                >
                  {e.phone}
                </a>
              ) : dash}
            </Row>
          </Section>

          <Section title={t("details_work")}>
            <Row icon={Briefcase} label={t("position")} testid="emp-detail-position">
              <span style={{ direction: "ltr", unicodeBidi: "embed" }}>{e.position || dash}</span>
            </Row>
            <Row icon={Building2} label={t("branch")} testid="emp-detail-branch">
              {onVacation ? (
                <span className="pill bg-amber-100 text-amber-800 border border-amber-300">
                  🏖️ {t("on_vacation")}
                </span>
              ) : (
                <>
                  {branch?.code ? <span className="text-gray-500 text-xs me-1">[{branch.code}]</span> : null}
                  {branch?.name || dash}
                </>
              )}
            </Row>
            <Row icon={UserCog} label={t("admin")} testid="emp-detail-admin">
              {onVacation ? dash : (adminName || dash)}
            </Row>
          </Section>

          {!hideCerts && (
          <Section title={t("details_certificates")}>
            <Row icon={ShieldCheck} label={t("health_expiry_col")} testid="emp-detail-health">
              <div className="flex flex-col items-end gap-1">
                <span className="text-gray-900">{formatHijri(e.health_expiry_hijri) || dash}</span>
                <StatusBadge hijri={e.health_expiry_hijri} t={t} />
              </div>
            </Row>
            <Row icon={BookOpen} label={t("training_expiry_col")} testid="emp-detail-training">
              <div className="flex flex-col items-end gap-1">
                <span className="text-gray-900">{formatHijri(e.training_expiry_hijri) || dash}</span>
                <StatusBadge hijri={e.training_expiry_hijri} t={t} />
              </div>
            </Row>
            <Row icon={Building2} label={t("municipality_card")} testid="emp-detail-municipality">
              <BoolPill value={e.municipality_card} t={t} />
            </Row>
            <Row icon={User} label={t("printed_photo")} testid="emp-detail-printed">
              <BoolPill value={e.printed_photo} t={t} />
            </Row>
          </Section>
          )}

          {extraSections}

          {e.notes && (
            <Section title={t("notes")}>
              <div className="text-sm text-gray-700 whitespace-pre-wrap px-1 py-2 flex items-start gap-2" data-testid="emp-detail-notes">
                <StickyNote size={14} className="text-amber-600 mt-0.5 shrink-0" />
                <span className="break-words">{e.notes}</span>
              </div>
            </Section>
          )}

          <Section title={t("details_attachments")}>
            <div className="flex items-center justify-between px-1 py-1" data-testid="emp-detail-attachments-summary">
              <div className="flex items-center gap-2 text-gray-700">
                <Paperclip size={14} className="text-teal-700" />
                <span className="text-sm">{attachCount} {t("attachments")}</span>
              </div>
              {onOpenAttachments && (
                <button
                  type="button"
                  className="text-teal-700 text-sm font-semibold hover:underline"
                  onClick={() => onOpenAttachments(e)}
                  data-testid="emp-detail-open-attachments"
                >
                  {t("view_file")}
                </button>
              )}
            </div>
          </Section>
        </div>

        {/* Footer actions — sticky bottom for mobile reach */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-3 sm:px-6 py-2.5 flex items-center gap-2 z-10">
          {onEdit && (
            <button
              type="button"
              className="btn btn-primary flex-1 justify-center"
              onClick={() => onEdit(e)}
              data-testid="emp-details-edit-button"
            >
              <Edit3 size={14} /> {t("edit")}
            </button>
          )}
          {onOpenAttachments && (
            <button
              type="button"
              className="btn btn-secondary flex-1 justify-center relative"
              onClick={() => onOpenAttachments(e)}
              data-testid="emp-details-attach-button"
            >
              <Paperclip size={14} />
              <span>{t("attachments")}</span>
              {attachCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none">
                  {attachCount}
                </span>
              )}
            </button>
          )}
          {onMove && (
            <button
              type="button"
              className="btn btn-ghost text-indigo-700 hover:bg-indigo-50 justify-center"
              onClick={() => onMove(e)}
              data-testid="emp-details-move-button"
              title={t("move_to_area")}
            >
              <ArrowRightLeft size={14} /> <span className="hidden sm:inline">{t("move")}</span>
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="btn btn-ghost text-rose-600 hover:bg-rose-50 justify-center"
              onClick={() => onDelete(e)}
              data-testid="emp-details-delete-button"
              title={t("em_archive_employee")}
            >
              <Trash2 size={14} /> <span className="hidden sm:inline">{t("delete")}</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
